/**
 * Centralized TypeScript Type Definitions
 * All interfaces and types used across the application
 */

// User Types   
export interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'staff';
  status?: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
}

// Category Types 
export interface Category {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  items_count?: number;
  created_at?: string;
  updated_at?: string;
}

// Location Types (receiving = Pharmacy, Main store; issuing = ICU, Cath lab, etc.)
export interface Location {
  id: number;
  name: string;
  code?: string;
  type?: 'receiving' | 'issuing';
  description?: string;
  address?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Patient {
  id: number;
  name: string;
  identifier?: string;
  created_at?: string;
  updated_at?: string;
}

// Supplier Types
export interface Supplier {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Inventory Item Types 
export interface InventoryItem {
  id: number;
  name: string;
  barcode: string;
  sku?: string;
  category_id: number;
  category?: Category;
  location_id: number;
  location?: Location;
  quantity: number;
  min_quantity?: number;
  unit: string;
  unit_price?: number;
  cost_price?: number;
  status: 'active' | 'inactive';
  description?: string;
  created_at?: string;
  updated_at?: string;
  location_stocks?: LocationStock[]; 
}

// Per-location stock breakdown
export interface LocationStock {
  location_id: number;
  location_name: string;
  quantity: number;
}

// Stock Transaction Types 
export interface StockTransaction {
  id: number;
  item_id: number;
  item?: InventoryItem;
  type: 'receive' | 'issue' | 'adjust';
  quantity: number;
  location_id: number;
  location?: Location;
  to_location_id?: number | null;
  to_location?: Location | null;
  to_patient_id?: number | null;
  to_patient?: Patient | null;
  reference_number?: string;
  notes?: string;
  user_id: number;
  user?: User;
  created_at: string;
  updated_at?: string;
}

// Receive/Issue Item Types 
export interface ReceivedItem {
  item_id?: number;
  barcode: string;
  name: string;
  sku?: string;
  category?: string;
  category_id?: number;
  unit: string;
  quantity: number; // Total items (for API)
  packsBoxes?: number; // Number of packs/boxes (for display)
  itemsPerUnit?: number; // Items per pack/box (for display)
  location_id?: number;
  location?: string;
  unit_price?: number;
  cost_price?: number;
  batch_number: string;
  expiry_date?: string; // ISO date "YYYY-MM-DD"
  isNew: boolean;
  timestamp: string;
  newItemData?: {
    name: string;
    barcode: string;
    category_id: number;
    unit: string;
    unit_price?: number;
    cost_price?: number;
    min_quantity?: number;
    description?: string;
  };
}

export interface IssuedItem {
  item_id: number;
  barcode: string;
  name: string;
  sku?: string;
  category?: string;
  unit: string;
  quantity: number;
  packsBoxes?: number;
  itemsPerUnit?: number;
  available: number;
  timestamp: string;
}

/** Invoice line item (e.g. from issue-to-patient billing in JMD) */
export interface InvoiceLineItem {
  item_id?: number;
  name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  line_total: number;
  // Per-line discount fields
  discount_type?: 'percentage' | 'fixed' | null;
  discount_value?: number | null;
  discount_amount?: number;
  discounted_total?: number;
}

/** Invoice for patient issue (medications, currency JMD) */
export interface IssueInvoice {
  currency: string;
  currency_label?: string;
  status: 'open' | 'closed';
  line_items: InvoiceLineItem[];
  subtotal: number;
  total_discounts?: number;
  total: number;
}

/** Revenue report summary */
export interface RevenueReportSummary {
  gross_revenue: number;
  total_discounts: number;
  net_revenue: number;
  cogs: number;
  gross_profit: number;
  profit_margin: number;
  total_visits: number;
  total_transactions: number;
}

export interface RevenueSeriesRow {
  period: string;
  transactions: number;
  gross: number;
}

export interface RevenueReport {
  summary: RevenueReportSummary;
  series: RevenueSeriesRow[];
  date_from: string;
  date_to: string;
  group_by: 'day' | 'month';
}

/** Utilization report */
export interface UtilizationLocationRow {
  location_id: number;
  location_name: string;
  total_issued: number;
}

export interface UtilizationItemRow {
  item_id: number;
  item_name: string;
  item_unit: string;
  category: string | null;
  locations: UtilizationLocationRow[];
  grand_total: number;
}

export interface UtilizationReport {
  items: UtilizationItemRow[];
  date_from: string;
  date_to: string;
}

/** Detail row — one issued line per patient transaction */
export interface UtilizationDetailRow {
  date: string;
  transaction_ref: string;
  item_id: number;
  item_name: string;
  item_unit: string;
  category: string | null;
  quantity: number;
  location_id: number;
  location_name: string;
  patient_id: number | null;
  patient_name: string;
  patient_identifier: string;
  visit_reference: string;
  visit_type: string;
}

/** Sales report — summary (per visit) */
export interface SalesSummaryRow {
  date: string;
  visit_ref: string;
  patient_name: string;
  patient_identifier: string;
  visit_type: string;
  items_count: number;
  gross_total: number;
  discount_total: number;
  net_total: number;
}

/** Sales report — detail (per line item) */
export interface SalesDetailRow {
  date: string;
  visit_ref: string;
  patient_name: string;
  patient_identifier: string;
  visit_type: string;
  item_name: string;
  category: string;
  quantity: number;
  unit_price: number;
  line_gross: number;
  discount_type: 'percentage' | 'fixed' | null;
  discount_value: number | null;
  discount_amount: number;
  net_line: number;
}

/** Sales report — by category */
export interface SalesByCategoryRow {
  category: string;
  visit_count: number;
  total_qty: number;
  gross_revenue: number;
  discount_total: number;
  net_revenue: number;
}

/** Discounts report row */
export interface DiscountReportRow {
  date: string;
  visit_ref: string;
  patient_name: string;
  patient_identifier: string;
  visit_type: string;
  item_name: string;
  category: string;
  quantity: number;
  unit_price: number;
  line_gross: number;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  discount_amount: number;
  net_line: number;
}

/** A patient visit — groups all stock issues under one invoice */
export interface Visit {
  id: number;
  reference_number: string;
  status: 'open' | 'closed';
  visit_type: 'outpatient' | 'inpatient';
  patient_id: number;
  patient?: Patient;
  opened_by: number;
  opened_by_user?: User;
  closed_by?: number | null;
  closed_by_user?: User | null;
  closed_at?: string | null;
  notes?: string | null;
  invoice?: IssueInvoice;
  created_at?: string;
  updated_at?: string;
}

// Dashboard Types 
export interface DashboardStats {
  total_items: number;
  total_value: number;
  low_stock_items: number;
  recent_transactions: number;
  stock_movement?: StockMovementData[];
}

export interface StockMovementData {
  date: string;
  received: number;
  issued: number;
  balance: number;
}

export interface LowStockAlert {
  id: number;
  name: string;
  current: number;
  minimum: number;
  category: string;
}

export interface RecentActivity {
  id: number;
  type: 'receive' | 'issue' | 'adjust';
  item_name: string;
  quantity: number;
  user_name: string;
  created_at: string;
}

// Report Types 
export interface InventoryReport {
  total_items: number;
  total_value: number;
  low_stock_count: number;
  items: InventoryItem[];
}

export interface TransactionReport {
  total_transactions: number;
  total_received: number;
  total_issued: number;
  transactions: StockTransaction[];
}

// API Response Types 
export interface PaginatedResponse<T> {
  data: T[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from?: number;
    to?: number;
  };
  links?: {
    first?: string;
    last?: string;
    prev?: string | null;
    next?: string | null;
  };
  // Legacy support
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

// Form Types 
export interface CreateUserData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  role: 'admin' | 'staff';
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  password?: string;
  password_confirmation?: string;
  role?: 'admin' | 'staff';
  status?: 'active' | 'inactive';
}

export interface CreateCategoryData {
  name: string;
  description?: string;
  is_active?: boolean;
}

export interface UpdateCategoryData {
  name?: string;
  description?: string;
  is_active?: boolean;
}

export interface CreateLocationData {
  name: string;
  description?: string;
  address?: string;
}

export interface UpdateLocationData {
  name?: string;
  description?: string;
  address?: string;
}

export interface CreateInventoryItemData {
  name: string;
  barcode: string;
  sku?: string;
  category_id: number;
  location_id: number;
  quantity: number;
  min_quantity?: number;
  unit: string;
  unit_price?: number;
  cost_price?: number;
  description?: string;
}

export interface UpdateInventoryItemData {
  name?: string;
  barcode?: string;
  sku?: string;
  category_id?: number;
  location_id?: number;
  quantity?: number;
  min_quantity?: number;
  unit?: string;
  unit_price?: number;
  cost_price?: number;
  status?: 'active' | 'inactive';
  description?: string;
}

export interface ReceiveStockData {
  items: Array<{
    item_id?: number;
    barcode: string;
    name: string;
    category_id: number;
    location_id: number;
    quantity: number;
    unit_price?: number;
    min_quantity?: number;
    unit: string;
    sku?: string;
    description?: string;
  }>;
  notes?: string;
}

export interface IssueStockData {
  items: Array<{
    item_id: number;
    location_id: number;
    quantity: number;
  }>;
  notes?: string;
}

// Filter/Query Types 
export interface InventoryFilters {
  search?: string;
  category_id?: number;
  location_id?: number;
  status?: 'active' | 'inactive' | 'low_stock';
  page?: number;
  per_page?: number;
}

export interface CategoryFilters {
  search?: string;
  status?: 'active' | 'inactive';
  page?: number;
  per_page?: number;
}

export interface LocationFilters {
  search?: string;
  page?: number;
  per_page?: number;
}

export interface UserFilters {
  search?: string;
  role?: 'admin' | 'staff';
  status?: 'active' | 'inactive';
  page?: number;
  per_page?: number;
}

// Unit Types 
export type UnitOfMeasure = 
  | 'pcs' 
  | 'pack' 
  | 'packs' 
  | 'box' 
  | 'boxes' 
  | 'kg' 
  | 'g' 
  | 'l' 
  | 'ml' 
  | 'm' 
  | 'cm' 
  | 'unit' 
  | 'units';

export const UNITS: UnitOfMeasure[] = [
  'pcs',
  'pack',
  'packs',
  'box',
  'boxes',
  'kg',
  'g',
  'l',
  'ml',
  'm',
  'cm',
  'unit',
  'units',
];

export const PACK_BOX_UNITS: UnitOfMeasure[] = ['pack', 'packs', 'box', 'boxes'];

export function requiresItemsPerUnit(unit: string): boolean {
  return PACK_BOX_UNITS.includes(unit.toLowerCase() as UnitOfMeasure);
}

